/**
 * branding-kit — the Brand Kit's shared data layer and controls.
 *
 * Extracted 2026-09-18 so the ORIGINAL scrolling layout (`SettingsBranding`)
 * and the new All-Journeys-style three-pane designer (`branding-designer`) read
 * the SAME state, hydrate, save and scan. Nothing about the data seam changed in
 * the extraction — the hydrate effect, the `CONTRACT_FIELD` map, the awaited
 * `updateBranding` write and the guarded `scanBrandFromWebsite` resolution are
 * the 2026-09-03/09-08 implementations verbatim, moved.
 *
 * Data: partial — `getBranding` (read) and `updateBranding` (write) are declared
 * and implemented; `scanBrandFromWebsite` is declared, implemented and exported
 * from the client barrel but has NO serving binding. OBSERVED 2026-09-18 in this
 * design environment: `GET /v1/admin/settings/branding` → 501 ROUTE_UNBOUND and
 * `POST /v1/admin/settings/branding/scan` → 501 ROUTE_UNBOUND, so in the preview
 * the kit shows defaults and Publish reports an honest failure. Both are real
 * server contracts against a deployed backend; neither is faked here.
 *
 * Consumed ops: getBranding · updateBranding · scanBrandFromWebsite (REQ-076).
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { alpha, Btn, Card, Checkbox, Dropdown, Icon, IconBtn, Pill, T } from "@v0y4ge/jrni-ui";
import { logUpsert } from "../../lib/audit";
import { toast } from "../../shell/toast";
import { studioRequestOptions } from "../../lib/studio-config";
import { can } from "../../shell/access";
import { getBranding, removeBrandLogo, resetBranding, updateBranding, uploadBrandLogo } from "@v0y4ge/api-client";
import * as ApiClient from "@v0y4ge/api-client";
import { useT, t as i18nT } from "../../lib/i18n";
import { invalidateTenantChrome } from "../../lib/branded-chrome";

/* ── Fonts ────────────────────────────────────────────────────────── */
export const GOOGLE_FONTS = [ // ui-config
  "Inter","Roboto","Open Sans","Noto Sans","Lato","Montserrat","Poppins","Raleway","Nunito","Nunito Sans",
  "Work Sans","Rubik","Mulish","DM Sans","Manrope","Plus Jakarta Sans","Outfit","Sora","Figtree","Onest",
  "Public Sans","Source Sans 3","IBM Plex Sans","PT Sans","Karla","Hind","Fira Sans","Barlow","Barlow Condensed","Cabin",
  "Josefin Sans","Quicksand","Comfortaa","Titillium Web","Oxygen","Assistant","Heebo","Archivo","Archivo Narrow","Albert Sans",
  "Be Vietnam Pro","Epilogue","Lexend","Red Hat Display","Red Hat Text","Schibsted Grotesk","Space Grotesk","Kanit","Prompt","Chivo",
  "Overpass","Signika","Signika Negative","Jost","Urbanist","Instrument Sans","Hanken Grotesk","Bricolage Grotesque","Gabarito","Wix Madefor Text",
  "Libre Franklin","Maven Pro","Muli","Dosis","Exo","Exo 2","Saira","Saira Condensed","Encode Sans","Asap",
  "Merriweather","Playfair Display","Lora","PT Serif","Noto Serif","Source Serif 4","Bitter","Crimson Text","Crimson Pro","EB Garamond",
  "Cormorant","Cormorant Garamond","Libre Baskerville","Spectral","Domine","Zilla Slab","Roboto Slab","Slabo 27px","Arvo","Vollkorn",
  "Frank Ruhl Libre","Alegreya","Alegreya Sans","Cardo","Neuton","Lusitana","Bree Serif","Josefin Slab","Rokkitt","Old Standard TT",
  "Playfair Display SC","Fraunces","Newsreader","Petrona","Piazzolla","Literata","Gelasio","DM Serif Display","DM Serif Text","Marcellus",
  "Oswald","Bebas Neue","Anton","Archivo Black","Teko","Pathway Gothic One","Fjalla One","Staatliches","Righteous","Passion One",
  "Abril Fatface","Alfa Slab One","Bungee","Bungee Shade","Concert One","Lobster","Lobster Two","Pacifico","Sacramento","Satisfy",
  "Dancing Script","Great Vibes","Caveat","Kalam","Shadows Into Light","Indie Flower","Permanent Marker","Amatic SC","Patrick Hand","Gloria Hallelujah",
  "Cinzel","Cinzel Decorative","Philosopher","Yeseva One","Sorts Mill Goudy","Unna","Aleo","Cabin Condensed","Francois One","Yanone Kaffeesatz",
  "JetBrains Mono","Fira Code","Source Code Pro","IBM Plex Mono","Space Mono","Roboto Mono","Inconsolata","Ubuntu Mono","PT Mono","Courier Prime",
  "Ubuntu","Ubuntu Condensed","Cairo","Tajawal","Almarai","Markazi Text","Amiri","Noto Kufi Arabic","Noto Sans JP","Noto Serif JP",
  "M PLUS 1p","M PLUS Rounded 1c","Sawarabi Mincho","Zen Kaku Gothic New","Noto Sans KR","Noto Sans SC","Noto Sans TC","Prata","Playball","Cookie",
];
const _loadedFonts = new Set<string>();
export function ensureGoogleFont(family: string) {
  if (!family || _loadedFonts.has(family) || !GOOGLE_FONTS.includes(family)) return;
  _loadedFonts.add(family);
  const id = "gf-" + family.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id; link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

/* ── Contrast (WCAG 2.1 relative luminance) ───────────────────────────
 * jrni-ui's ColorPicker computes the same ratio but keeps its helpers
 * module-private, so this is a local implementation rather than a fork of an
 * export. If the DS ever exports one, delete this and import it. */
function hexChannels(hex: string): [number, number, number] | null {
  const h = hex.trim().replace(/^#/, "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
}
function luminance(ch: [number, number, number]): number {
  const [r, g, b] = ch.map((c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** Contrast ratio between two hex colours, or null if either is unparseable. */
export function contrastRatio(a: string, b: string): number | null {
  const ca = hexChannels(a), cb = hexChannels(b);
  if (!ca || !cb) return null;
  const la = luminance(ca), lb = luminance(cb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/* ── Scan review types ────────────────────────────────────────────── */
export type Confidence = "high" | "medium" | "low";
export interface ScanItem { key: string; group: string; label: string; kind: "color" | "font" | "text" | "image"; value: string; confidence?: Confidence; note?: string; accepted: boolean; }
export const CONF_TONE: Record<Confidence, { tone: "success" | "warn" | "neutral"; label: string; labelKey: string }> = {
  high: { tone: "success", label: "High confidence", labelKey: "settings.branding.highConfidence" },
  medium: { tone: "warn", label: "Inferred", labelKey: "settings.branding.inferred" },
  low: { tone: "neutral", label: "Low confidence", labelKey: "settings.branding.lowConfidence" },
};

export const DEFAULT_BRAND = {
  name: "", tagline: "", boilerplate: "", mailingAddress: "",
  primary: "#5142C7", secondary: "#14102E", accent: "#D01647", link: "#3A6FE0", cover: "#14102E",
  success: "#1F8A5B", warning: "#C77F14", error: "#C0392B", info: "#2477A8",
  neutralText: "#14131F", neutralBg: "#F6F7F9",
  headingFont: "Inter", bodyFont: "Inter", headingWeight: "700", bodyWeight: "400", fontFallback: "system-ui, sans-serif",
  buttonRadius: "8", buttonStyle: "solid",
  senderName: "", senderEmail: "", replyTo: "", smsSender: "", unsubscribeText: "Unsubscribe from these emails",
  /* JAI-1376 — the registered entity name that appears in the email footer's
     "sent by" line. Distinct from `senderName` (the friendly From name) and
     from `name` (the brand's display name): a footer states who legally sent
     the mail, which is the one place a customer looks to verify it. Empty falls
     back to the sender name at send time — stated in the field's hint, because a
     silent fallback reads as "the setting does nothing". */
  legalName: "",
  /* Custom header / footer (2026-09-18, operator). `kit` renders the header and
     footer FROM the fields above (logo, colours, footer text); `custom` serves
     the author's own markup. Three plain strings rather than a nested object, so
     the existing dirty-check and PATCH diff keep working unchanged.

     Stored on the branding record since 2026-09-23 (headerMode/headerHtml/
     footerMode/footerHtml/customCss — CD-API-REQUEST-2026-09-23-branded-chrome,
     which supersedes the 2026-09-18 ask) and sent through CONTRACT_FIELD. Journeys
     that opt in to the branded header/footer draw these. */
  headerMode: "kit", headerHtml: "", footerMode: "kit", footerHtml: "", customCss: "",
  website: "", instagram: "", facebook: "", linkedin: "", twitter: "",
  privacyUrl: "", termsUrl: "", poweredBy: true, footer: "Powered by JRNI",
  appleWalletEnabled: false, googleWalletEnabled: false,
};
export type Brand = typeof DEFAULT_BRAND;
export const EMPTY_ASSETS = { logo: null, logoReverse: null, logoIcon: null, favicon: null, appleTouch: null, pwa192: null, pwa512: null, ogImage: null, emailLogo: null } as Record<string, string | null>;

/* ── Small UI helpers (shared by both layouts) ────────────────────── */
export function Section({ title, desc, children, span }: { title: string; desc?: string; children: ReactNode; span?: boolean }) {
  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16, gridColumn: span ? "1 / -1" : undefined }}>
      <div style={{ borderBottom: `1px solid ${T.border}`, paddingBottom: 14, marginBottom: 2 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{title}</div>
        {desc ? <div style={{ fontSize: 13, color: T.soft, marginTop: 4 }}>{desc}</div> : null}
      </div>
      {children}
    </Card>
  );
}
export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n2 }}>{label}</div>
      {children}
      {hint ? <div style={{ fontSize: 12, color: T.soft }}>{hint}</div> : null}
    </div>
  );
}

/** Screen-reader-only text: in the a11y tree, off the screen. */
export const SR_ONLY: CSSProperties = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 };

/** Polite live region — save / scan / upload outcomes reach screen readers.
 *  Toasts are visual only; this is the announcement channel. */
export function LiveRegion({ text }: { text: string }) {
  return <div aria-live="polite" aria-atomic="true" role="status" style={SR_ONLY}>{text}</div>;
}

/**
 * ColorRow — swatch + hex entry + a WCAG readout.
 *
 * a11y (2026-09-18): the swatch and the hex box are separate controls for the
 * same value, so BOTH carry their own accessible name (an unlabelled
 * `<input type="color">` announces as "colour picker" and nothing else). The
 * contrast readout is text, never colour alone — a red pill saying nothing else
 * is invisible to a screen reader and to anyone who can't separate red from
 * green. `against` is the surface the colour is actually used on.
 */
export function ColorRow({ label, hint, value, onChange, canWrite, against, againstLabel, large }: {
  label: string; hint?: string; value: string; onChange: (v: string) => void; canWrite: boolean;
  against?: string; againstLabel?: string; large?: boolean;
}) {
  const t = useT();
  const ratio = against ? contrastRatio(value, against) : null;
  const threshold = large ? 3 : 4.5;
  const pass = ratio != null && ratio >= threshold;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
      <input type="color" value={value} onChange={(e) => canWrite && onChange(e.target.value)} disabled={!canWrite}
        aria-label={t("settings.brandingKit.ariaLabel", "{label} — {arg2}", { label, arg2: t("settings.brandKit.colourSwatch", "colour swatch") })}
        style={{ width: 34, height: 34, padding: 0, border: `1px solid ${T.border}`, borderRadius: 8, cursor: canWrite ? "pointer" : "not-allowed", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{label}</div>
        {hint ? <div style={{ fontSize: 11.5, color: T.soft }}>{hint}</div> : null}
      </div>
      {ratio != null ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.n2, whiteSpace: "nowrap" }}>
          <span style={{ fontFamily: "monospace" }}>{ratio.toFixed(2)}:1</span>
          <Pill tone={pass ? "success" : "danger"}>{pass
            ? t("settings.brandKit.passesAa", "Passes AA")
            : t("settings.brandKit.failsAa", "Fails AA")}</Pill>
          <span style={SR_ONLY}>{againstLabel ?? ""}</span>
        </span>
      ) : null}
      <input value={value.toUpperCase()} onChange={(e) => canWrite && onChange(e.target.value)} disabled={!canWrite}
        aria-label={t("settings.brandingKit.ariaLabel", "{label} — {arg2}", { label, arg2: t("settings.brandKit.hexValue", "hex value") })}
        style={{ width: 92, height: 32, padding: "0 8px", border: `1px solid ${T.n4}`, borderRadius: 6, fontSize: 12.5, fontFamily: "monospace", color: T.n2, textAlign: "center", background: T.white }} />
    </div>
  );
}

/**
 * ImageDrop — drag-and-drop or browse, keyboard-operable.
 *
 * a11y (2026-09-18): the dropzone was a bare `<div onClick>` — unreachable by
 * keyboard, with no accessible name, and with the Remove button nested inside
 * the click target. It is now a real `<button>` naming what it uploads and
 * describing the size rule through `aria-describedby`; a filled slot renders a
 * plain panel with separate Replace / Remove buttons (no nested interactives).
 */
export function ImageDrop({ label, hint, value, onFile, onClear, canWrite, dark, tall }: {
  label?: string; hint: string; value: string | null; onFile: (f: File) => void; onClear: () => void; canWrite: boolean; dark?: boolean; tall?: boolean;
}) {
  const t = useT();
  const ref = useRef<HTMLInputElement>(null);
  const [hintId] = useState(() => "imgdrop-hint-" + Math.random().toString(36).slice(2, 8));
  const name = label ?? t("settings.brandKit.image", "Image");
  const frame: CSSProperties = {
    border: `2px dashed ${T.n4}`, borderRadius: 12, padding: tall ? 24 : 16, textAlign: "center",
    background: dark ? "#14102E" : T.n6, display: "flex", flexDirection: "column", alignItems: "center",
    gap: 10, minHeight: tall ? 120 : 96, justifyContent: "center", width: "100%", boxSizing: "border-box",
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}
      onDrop={(e) => { e.preventDefault(); if (canWrite && e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
      onDragOver={(e) => e.preventDefault()}>
      {label ? <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n2 }}>{label}</div> : null}
      {value ? (
        <div style={{ ...frame, borderStyle: "solid", borderColor: T.border }}>
          <img src={value} alt={t("settings.brandingKit.alt", "{name} — {arg2}", { name, arg2: t("settings.brandKit.currentImage", "current image") })} style={{ maxHeight: tall ? 72 : 44, maxWidth: "100%", objectFit: "contain" }} />
          {canWrite ? (
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="secondary" size="sm" icon={<Icon.upload size={12} />} onClick={() => ref.current?.click()}>{t("settings.brandKit.replace", "Replace")}</Btn>
              <Btn variant="ghost" size="sm" icon={<Icon.trash size={12} />} onClick={onClear}>{t("common.actions.remove", "Remove")}</Btn>
            </div>
          ) : null}
        </div>
      ) : (
        <button type="button" disabled={!canWrite} onClick={() => ref.current?.click()} aria-describedby={hintId}
          style={{ ...frame, cursor: canWrite ? "pointer" : "not-allowed", fontFamily: T.font }}>
          <span style={{ width: 40, height: 40, borderRadius: 10, background: dark ? alpha("#fff", "18") : T.p4, display: "inline-flex", alignItems: "center", justifyContent: "center" }} aria-hidden="true"><Icon.upload size={17} style={{ color: dark ? "#fff" : T.p1 }} /></span>
          <span style={{ fontSize: 12.5, color: dark ? alpha("#fff", "88") : T.n2, fontWeight: 500 }}>{t("settings.brandingKit.text", "{arg1} {name}", { arg1: t("settings.brandKit.uploadAction", "Upload"), name })}</span>
          <span style={{ fontSize: 11.5, color: dark ? alpha("#fff", "60") : T.soft }}>{t("settings.brandKit.dropOrPressToBrowse", "Drop a file here, or press to browse")}</span>
        </button>
      )}
      <div id={hintId} style={{ fontSize: 11.5, color: T.soft }}>{hint}</div>
      <input ref={ref} type="file" accept="image/*" style={SR_ONLY} tabIndex={-1} aria-hidden="true"
        onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); e.currentTarget.value = ""; }} />
    </div>
  );
}

/* ── Font control (Google + upload) ───────────────────────────────── */
export function FontControl({ label, value, onChange, canWrite, customFonts, onUpload }: {
  label: string; value: string; onChange: (v: string) => void; canWrite: boolean; customFonts: { name: string }[]; onUpload: (file: File) => void;
}) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  /* The font groups ride on each option's supporting text (jrni-ui Dropdown has no optgroup). The
     "Google Fonts" line is shown only when there is a second group to tell it apart from. */
  const fontOptions = [
    ...customFonts.map((f) => ({ value: f.name, label: f.name, supportingText: t("settings.branding.yourFonts", "Your fonts") })),
    ...GOOGLE_FONTS.map((f) => ({ value: f, label: f, ...(customFonts.length ? { supportingText: t("settings.branding.googleFonts", "Google Fonts") } : {}) })),
  ];
  return (
    <Field label={label} hint={t("settings.branding.hint", "Pick a Google Font or upload your own (.woff2, .woff, .ttf, .otf).")}>
      <div style={{ display: "flex", gap: 8 }}>
        <Dropdown<string> portal aria-label={label} value={value} onChange={(v) => canWrite && onChange(v)} disabled={!canWrite} options={fontOptions} style={{ flex: 1, minWidth: 0 }} />
        <Btn variant="secondary" icon={<Icon.upload size={14} />} disabled={!canWrite} onClick={() => inputRef.current?.click()}>{t("settings.branding.upload", "Upload")}</Btn>
        <input ref={inputRef} type="file" accept=".woff2,.woff,.ttf,.otf,font/woff,font/woff2,font/ttf,font/otf" style={SR_ONLY} tabIndex={-1} aria-hidden="true" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.currentTarget.value = ""; }} />
      </div>
    </Field>
  );
}

/**
 * ScanReviewModal — the per-item accept/reject list for a returned scan.
 *
 * a11y (2026-09-18): each row was a `<button>` drawing a checkbox glyph, so
 * assistive tech heard "button" and never the checked state. Rows are now the
 * DS `Checkbox` (role="checkbox", aria-checked, space/enter) inside a labelled
 * row; the dialog carries role="dialog" + aria-modal + Esc.
 */
export function ScanReviewModal({ items, onToggle, onSetAll, onApply, onClose }: {
  items: ScanItem[]; onToggle: (key: string) => void; onSetAll: (accepted: boolean) => void; onApply: () => void; onClose: () => void;
}) {
  const t = useT();
  const titleId = "brandscan-title";
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={(e) => e.stopPropagation()}
        style={{ width: 620, maxWidth: "96vw", maxHeight: "86vh", background: T.white, borderRadius: 16, boxShadow: "0 24px 60px -12px rgba(16,24,40,.4)", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: T.font }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div id={titleId} style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{t("settings.branding.reviewDetectedBrandAssets", "Review detected brand assets")}</div>
            <div style={{ fontSize: 12.5, color: T.soft, marginTop: 2 }}>{t("settings.brandKit.everyRowIsAValueTheServerReturned", "Every row is a value the server returned. Accept what looks right — nothing is applied until you do.")}</div>
          </div>
          <IconBtn label={t("common.actions.close", "Close")} onClick={onClose}><Icon.x size={16} /></IconBtn>
        </div>
        <div style={{ padding: "8px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: T.soft }}>{items.filter((i) => i.accepted && i.value).length} {t("settings.branding.of", "of")}{" "}{items.filter((i) => i.value).length} {t("settings.branding.selected", "selected")}</span>
          <span style={{ flex: 1 }} />
          <Btn variant="ghost" size="sm" onClick={() => onSetAll(true)}>{t("settings.branding.selectAll", "Select all")}</Btn>
          <Btn variant="ghost" size="sm" onClick={() => onSetAll(false)}>{t("common.actions.clear", "Clear")}</Btn>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "10px 16px 16px" }}>
          {["Identity", "Logo", "Icons", "Colours", "Typography", "Social", "Communications"].map((group) => {
            const rows = items.filter((i) => i.group === group);
            if (!rows.length) return null;
            return (
              <div key={group} style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, padding: "6px 4px" }}>{group}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {rows.map((it) => {
                    const found = !!it.value;
                    const conf = it.confidence ? CONF_TONE[it.confidence] : null;
                    const rowId = `brandscan-row-${it.key}`;
                    return (
                      <div key={it.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: `1px solid ${it.accepted && found ? T.p2 : T.border}`, background: it.accepted && found ? T.p4 : T.white, borderRadius: 10, opacity: found ? 1 : 0.55 }}>
                        <Checkbox checked={it.accepted && found} disabled={!found} aria-labelledby={rowId}
                          onChange={found ? () => onToggle(it.key) : undefined} />
                        {it.kind === "color" ? <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 6, background: it.value, border: `1px solid ${T.border}`, flexShrink: 0 }} /> : null}
                        {it.kind === "image" && found ? <img src={it.value} alt="" style={{ width: 26, height: 26, borderRadius: 6, objectFit: "contain", flexShrink: 0, background: T.n6 }} /> : null}
                        <span style={{ flex: 1, minWidth: 0 }} id={rowId}>
                          <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{it.label}</span>
                          <span style={{ display: "block", fontSize: 11.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{found ? (it.kind === "color" || it.kind === "text" || it.kind === "font" ? it.value : it.note || t("settings.settingsBranding.detected", "Detected")) : t("settings.settingsBranding.notFoundAddItManually", "Not found — add it manually")}</span>
                        </span>
                        {found && conf ? <Pill tone={conf.tone}>{t(conf.labelKey, conf.label)}</Pill> : found ? <Pill tone="neutral">{t("settings.settingsBranding.fromScan", "From scan")}</Pill> : <Pill tone="neutral">{t("settings.settingsBranding.notFound", "Not found")}</Pill>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ padding: "12px 18px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11.5, color: T.soft, maxWidth: 320, lineHeight: 1.4 }}>{t("settings.branding.vectorLogosLicensedFontsAndBrandRulesCanTBeScrapedAdd", "Vector logos, licensed fonts and brand rules can't be scraped — add those by hand.")}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn icon={<Icon.check size={14} />} onClick={onApply}>{t("settings.branding.applySelected", "Apply selected")}</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── The kit's state, hydrate, save and scan ──────────────────────── */

/* PATCH takes a partial, so only CHANGED fields are sent, under their contract
   names. Our form's names are the screen's own vocabulary; this map is the one
   place the two meet. Fields with no contract home yet (secondary / success /
   warning / error, social handles, tagline, SMS sender, unsubscribe text,
   mailing address, uploads) are NOT
   sent — they would be dropped silently, so
   they wait until they are modelled and named. */
const CONTRACT_FIELD: Partial<Record<keyof Brand, string>> = {
  primary: "brandColor", accent: "accentColor", cover: "coverColor",
  headingFont: "headingFont", bodyFont: "bodyFont",
  senderName: "emailSenderName", legalName: "legalName",
  footer: "publicFooterText",
  appleWalletEnabled: "appleWalletEnabled", googleWalletEnabled: "googleWalletEnabled",
};

export type BrandKit = ReturnType<typeof useBrandKit>;

export function useBrandKit() {
  const t = useT();
  const canWrite = can("branding.write");
  const [brand, setBrand] = useState<Brand>({ ...DEFAULT_BRAND });
  const [assets, setAssets] = useState<Record<string, string | null>>({ ...EMPTY_ASSETS });
  const [customFonts, setCustomFonts] = useState<{ name: string }[]>([]);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [legalNameSupported, setLegalNameSupported] = useState(false);
  const [brandKitDesigner, setBrandKitDesigner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [readFailed, setReadFailed] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [_initial, setInitial] = useState(() => JSON.stringify({ b: DEFAULT_BRAND, a: EMPTY_ASSETS }));

  const announce = (msg: string) => setAnnouncement(msg);

  useEffect(() => {
    // voyage#58: hydrate the FULL kit from the API. The old effect gated on
    // `b.primaryColor` — but the contract's canonical field is `brandColor`
    // (BrandingSettings), so a real tenant kit hydrated nothing and the form
    // showed blank dropzones/default colors while the API returned the kit.
    getBranding(studioRequestOptions("settings.branding") as never).then((r: any) => {
      const b = r.branding ?? r.data ?? r;
      if (!b || typeof b !== "object") return;
      const pick = (...vals: unknown[]) => vals.find((v) => v !== undefined && v !== null && v !== "") as any;
      const hydrated: Brand = {
        ...DEFAULT_BRAND,
        name: pick(b.name, b.workspaceName, DEFAULT_BRAND.name),
        primary: pick(b.brandColor, b.primaryColor, b.primary, DEFAULT_BRAND.primary),
        secondary: pick(b.secondary, DEFAULT_BRAND.secondary),
        accent: pick(b.accentColor, b.accent, DEFAULT_BRAND.accent),
        cover: pick(b.coverColor, b.cover, DEFAULT_BRAND.cover),
        success: pick(b.success, DEFAULT_BRAND.success),
        warning: pick(b.warning, DEFAULT_BRAND.warning),
        error: pick(b.danger, DEFAULT_BRAND.error),
        headingFont: pick(b.headingFont, DEFAULT_BRAND.headingFont),
        bodyFont: pick(b.bodyFont, DEFAULT_BRAND.bodyFont),
        buttonRadius: b.radius != null ? String(b.radius) : DEFAULT_BRAND.buttonRadius,
        senderName: pick(b.emailSenderName, b.senderName, DEFAULT_BRAND.senderName),
        legalName: pick(b.legalName, DEFAULT_BRAND.legalName),
        privacyUrl: pick(b.privacyUrl, DEFAULT_BRAND.privacyUrl),
        termsUrl: pick(b.termsUrl, DEFAULT_BRAND.termsUrl),
        poweredBy: typeof b.poweredBy === "boolean" ? b.poweredBy : DEFAULT_BRAND.poweredBy,
        footer: pick(b.publicFooterText, b.footer, DEFAULT_BRAND.footer),
        boilerplate: pick(b.voice, DEFAULT_BRAND.boilerplate),
        headerMode: b.headerMode === "custom" ? "custom" : "kit",
        headerHtml: typeof b.headerHtml === "string" ? b.headerHtml : "",
        footerMode: b.footerMode === "custom" ? "custom" : "kit",
        footerHtml: typeof b.footerHtml === "string" ? b.footerHtml : "",
        customCss: typeof b.customCss === "string" ? b.customCss : "",
        appleWalletEnabled: b.appleWalletEnabled === true,
        googleWalletEnabled: b.googleWalletEnabled === true,
      };
      const hydratedAssets: Record<string, string | null> = { ...EMPTY_ASSETS, logo: b.logoUrl || null, favicon: b.faviconUrl || null, ogImage: b.ogImage || null };
      setBrand(hydrated);
      setAssets(hydratedAssets);
      /* JAI-1376 ships `legalName` on the read model. Until it is deployed the
         field is dropped by the backend, so the control is DISABLED rather than
         offered — a Save that cannot store what was typed is the defect this
         whole exchange started from. Presence of the key (even null) is the
         signal; a value is not required. */
      setLegalNameSupported(Object.prototype.hasOwnProperty.call(b, "legalName"));
      /* JAI-1886: served layout switch. Optional read until the kit carries the
         field; anything but a literal true keeps the scrolling layout. */
      setBrandKitDesigner((b as { brandKitDesigner?: unknown }).brandKitDesigner === true);
      // Re-baseline the dirty check so a freshly-hydrated kit isn't "unsaved".
      setInitial(JSON.stringify({ b: hydrated, a: hydratedAssets }));
      ensureGoogleFont(hydrated.headingFont);
      ensureGoogleFont(hydrated.bodyFont);
    }).catch((e) => {
      /* An unbound or failing read leaves the form on DEFAULT_BRAND. That has to
         be SAID — a kit of defaults presented as the tenant's kit is the same
         dishonesty class as a fake save. The designer layout surfaces this. */
      console.warn("getBranding failed:", e);
      setReadFailed(true);
    });
  }, []);

  const [scanUrl, setScanUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<ScanItem[] | null>(null);

  const set = (patch: Partial<Brand>) => setBrand((b) => ({ ...b, ...patch }));
  const setAsset = (key: string, v: string | null) => {
    if (key === "logo" && v === null) setPendingLogoFile(null);
    setAssets((a) => ({ ...a, [key]: v }));
  };
  const dirty = JSON.stringify({ b: brand, a: assets }) !== _initial;

  useEffect(() => { ensureGoogleFont(brand.headingFont); ensureGoogleFont(brand.bodyFont); }, [brand.headingFont, brand.bodyFont]);

  /* Leaving with unsaved drafts (2026-09-18, operator). The browser's own
     confirm is the only guard that covers a reload, a closed tab and a typed
     URL alike. In-app route changes are NOT covered: the shell's router has no
     navigation-blocking seam, so that half of the ask needs a router change —
     declared in the changelog rather than faked with a link-click interceptor
     that misses programmatic navigation. */
  useEffect(() => {
    if (!dirty) return;
    const onBefore = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBefore);
    return () => window.removeEventListener("beforeunload", onBefore);
  }, [dirty]);

  /* ── Uploaders ──────────────────────────────────────────────────── */
  const readAsDataUrl = (file: File, cb: (dataUrl: string) => void) => {
    const reader = new FileReader(); reader.onload = (e) => cb(e.target?.result as string); reader.readAsDataURL(file);
  };
  const uploadImage = (key: string, file: File, label: string) => readAsDataUrl(file, (d) => {
    if (key === "logo") setPendingLogoFile(file);
    setAsset(key, d);
    toast({ tone: "success", title: i18nT("settings.branding.labelUploaded.icu", "{value0} uploaded", { value0: label }), subtitle: i18nT("settings.branding.fileNameSavedToYourBrandKit.icu", "{value0} · saved to your brand kit.", { value0: file.name }) });
    announce(i18nT("settings.branding.labelUploaded.icu", "{value0} uploaded", { value0: label }));
  });
  // Size-restricted uploader (exact px pairs; SVG passes as vector).
  const uploadSized = (key: string, file: File, label: string, allowed: [number, number][]) => {
    const isSvg = file.type === "image/svg+xml" || /\.svg$/i.test(file.name);
    readAsDataUrl(file, (dataUrl) => {
      if (isSvg) {
        setAsset(key, dataUrl);
        toast({ tone: "success", title: i18nT("settings.branding.labelUploaded.icu", "{value0} uploaded", { value0: label }), subtitle: i18nT("settings.branding.fileNameVector.icu", "{value0} · vector.", { value0: file.name }) });
        announce(i18nT("settings.branding.labelUploaded.icu", "{value0} uploaded", { value0: label }));
        return;
      }
      const img = new Image();
      img.onload = () => {
        const ok = allowed.some(([w, h]) => img.width === w && img.height === h);
        if (!ok) {
          const title = i18nT("settings.branding.wrongLabelTolowercaseSize.icu", "Wrong {value0} size", { value0: label.toLowerCase() });
          const subtitle = i18nT("settings.branding.mustBeAllowedMapWHWHJoinOrPxThat.icu", "Must be {value0} px — that image is {value1}×{value2}.", { value0: allowed.map(([w, h]) => `${w}×${h}`).join(" or "), value1: img.width, value2: img.height });
          toast({ tone: "danger", title, subtitle });
          announce(`${title} — ${subtitle}`);
          return;
        }
        setAsset(key, dataUrl);
        toast({ tone: "success", title: i18nT("settings.branding.labelUploaded.icu", "{value0} uploaded", { value0: label }), subtitle: i18nT("settings.branding.imgWidthImgHeightSaved.icu", "{value0}×{value1} · saved.", { value0: img.width, value1: img.height }) });
        announce(i18nT("settings.branding.labelUploaded.icu", "{value0} uploaded", { value0: label }));
      };
      img.onerror = () => {
        const title = t("settings.branding.couldnTReadThatImage", "Couldn't read that image");
        toast({ tone: "danger", title });
        announce(title);
      };
      img.src = dataUrl;
    });
  };
  const handleFontUpload = (file: File, target: "headingFont" | "bodyFont") => {
    const family = file.name.replace(/\.(woff2?|ttf|otf)$/i, "").replace(/[-_]+/g, " ").trim() || "Custom font";
    readAsDataUrl(file, async (dataUrl) => {
      try {
        const face = new FontFace(family, `url(${dataUrl})`); await face.load(); (document.fonts as FontFaceSet).add(face);
        setCustomFonts((prev) => prev.some((f) => f.name === family) ? prev : [...prev, { name: family }]);
        set({ [target]: family } as Partial<Brand>);
        toast({ tone: "success", title: t("settings.branding.fontUploaded", "Font uploaded"), subtitle: t("settings.branding.familyAppliedToTargetHeadingfontHeadingsBody.icu", "\"{family}\" applied to {target, select, headingFont {headings} other {body}}.", { family, target }) });
        announce(t("settings.branding.fontUploaded", "Font uploaded"));
      } catch {
        const title = t("settings.branding.couldnTLoadThatFont", "Couldn't load that font");
        toast({ tone: "danger", title, subtitle: t("settings.branding.useAWoff2WoffTtfOrOtfFile", "Use a .woff2, .woff, .ttf, or .otf file.") });
        announce(title);
      }
    });
  };

  /* ── Website scan → review list ───────────────────────────────────
   * UIR-19 (2026-07-25): this used to FABRICATE a scan — a setTimeout "delay",
   * colours hashed from the hostname, fonts picked out of GOOGLE_FONTS, and
   * invented provenance notes ("From <title> / og:site_name"). It calls the real
   * declared op; every row shown is a value the server actually returned.
   *
   * 2026-09-18: `scanBrandFromWebsite` IS now exported from the client barrel
   * (api-client/src/index.ts), so the typeof guard below passes and the call
   * goes out. OBSERVED: POST /v1/admin/settings/branding/scan → 501
   * ROUTE_UNBOUND in this design environment, which lands in the catch and
   * reports an honest failure. The guard stays for a build where the export is
   * absent. No invented results on any path. */
  const runScan = async () => {
    if (!scanUrl.trim() || scanning) return;
    setScanning(true);
    setScanError(null);
    announce(t("settings.settingsBranding.scanning", "Scanning…"));
    const scanOp = (ApiClient as unknown as Record<string, unknown>).scanBrandFromWebsite as
      ((input: { url: string }, options: unknown) => Promise<{ draft?: Record<string, string> }>) | undefined;
    if (typeof scanOp !== "function") {
      const msg = t("settings.branding.theScanEndpointIsnTAvailableInThisBuild", "The scan endpoint isn't available in this build (REQ-076) — nothing was imported.");
      setScanError(msg);
      announce(msg);
      setScanning(false);
      return;
    }
    try {
      const res = await scanOp({ url: scanUrl.trim() }, studioRequestOptions("settings.branding") as never);
      const d = res?.draft ?? {};
      const row = (key: string, group: string, label: string, kind: ScanItem["kind"], value: unknown): ScanItem | null =>
        (typeof value === "string" && value.trim()) ? { key, group, label, kind, value, accepted: true } : null;
      const items = [
        row("name", "Identity", t("settings.branding.companyName", "Company name"), "text", d.name),
        row("website", "Identity", t("settings.branding.website", "Website"), "text", d.domain),
        row("primary", "Colours", t("settings.branding.primaryColour", "Primary color"), "color", d.primary),
        row("secondary", "Colours", t("settings.branding.secondaryColour", "Secondary color"), "color", d.secondary),
        row("accent", "Colours", t("settings.branding.accentColour", "Accent color"), "color", d.accent),
        row("cover", "Colours", t("settings.branding.coverColour", "Cover colour"), "color", d.cover),
        row("headingFont", "Typography", t("settings.branding.headingFont", "Heading font"), "font", d.headingFont),
        row("bodyFont", "Typography", t("settings.branding.bodyFont", "Body font"), "font", d.bodyFont),
        row("senderName", "Communications", t("settings.branding.emailSenderName", "Email sender name"), "text", d.senderName),
        row("footer", "Communications", t("settings.branding.footerText", "Footer text"), "text", d.footer),
      ].filter(Boolean) as ScanItem[];
      setScanResult(items);
      if (!items.length) {
        const msg = t("settings.branding.theScanReturnedNothingUsable", "The scan returned nothing usable for that address.");
        setScanError(msg);
        announce(msg);
      } else {
        announce(t("settings.brandKit.scanFinished", "The scan finished — review what it found."));
      }
    } catch (e) {
      console.warn("scanBrandFromWebsite failed:", e);
      setScanResult(null);
      const msg = t("settings.branding.couldnTScanThatWebsite", "Couldn't scan that website — nothing was imported.");
      setScanError(msg);
      announce(msg);
    } finally {
      setScanning(false);
    }
  };

  const toggleScanItem = (key: string) => setScanResult((items) => items ? items.map((it) => it.key === key ? { ...it, accepted: !it.accepted } : it) : items);
  const setAllScan = (accepted: boolean) => setScanResult((items) => items ? items.map((it) => it.value ? { ...it, accepted } : it) : items);
  const applyScan = () => {
    if (!scanResult) return;
    const bp: Partial<Brand> = {}; let applied = 0;
    for (const it of scanResult) {
      if (!it.accepted || !it.value) continue;
      applied++;
      if (it.kind === "image") { setAsset(it.key, it.value); continue; }
      if (it.kind === "font") ensureGoogleFont(it.value);
      (bp as Record<string, string>)[it.key] = it.value;
    }
    if (Object.keys(bp).length) set(bp);
    setScanResult(null);
    toast({ tone: "success", title: t("settings.branding.appliedAppliedBrandAssetApplied1S.icu", "Applied {count, plural, one {# brand asset} other {# brand assets}}", { count: applied }), subtitle: t("settings.branding.reviewTheKitAndSaveChanges", "Review the kit and Save changes.") });
    announce(t("settings.branding.appliedAppliedBrandAssetApplied1S.icu", "Applied {count, plural, one {# brand asset} other {# brand assets}}", { count: applied }));
  };

  const changedPayload = (): Record<string, unknown> => {
    const before = JSON.parse(_initial).b as Brand;
    const out: Record<string, unknown> = {};
    (Object.keys(CONTRACT_FIELD) as (keyof Brand)[]).forEach((k) => {
      if (brand[k] === before[k]) return;
      if (k === "legalName" && !legalNameSupported) return;
      const field = CONTRACT_FIELD[k] as string;
      /* Empty stores null (their spec for `legalName`; the same reading is right
         for every optional string — "" is not a value a footer should print). */
      out[field] = typeof brand[k] === "string" ? ((brand[k] as string).trim() || null) : brand[k];
    });
    return out;
  };
  /** How many STORED fields a Publish would write. Uploads and unmodelled
   *  fields are deliberately excluded — they are not persisted yet, so counting
   *  them would overstate what Publish does. */
  const logoChanged = assets.logo !== (JSON.parse(_initial).a as Record<string, string | null>).logo;
  const draftCount = Object.keys(changedPayload()).length + (logoChanged ? 1 : 0);

  /* ── Publish (was Save, 2026-09-03) ──────────────────────────────────────
     `updateBranding` (PATCH /v1/admin/settings/branding) existed all along and
     this screen never called it: Save was `logUpsert` + a toast, so the whole
     Brand Kit reported success and stored nothing (CC 2026-09-03). The write is
     awaited BEFORE any success feedback, and the audit entry is written from a
     successful response — ONE entry per publish, not per field. */
  const save = async () => {
    if (saving) return;
    const patch = changedPayload();
    if (!Object.keys(patch).length && !logoChanged) {
      toast({ tone: "info", title: t("settings.branding.nothingToSave", "Nothing to save"), subtitle: t("settings.branding.nothingToSaveBody", "No stored field changed — uploads and unmodelled fields are not persisted yet.") });
      announce(t("settings.branding.nothingToSave", "Nothing to save"));
      return;
    }
    setSaving(true);
    try {
      /* `updateBranding(input, options)` takes TWO arguments and was being called
         with one (2026-09-08) — so the write went out with no baseUrl. Passing
         `studioRequestOptions()`, the same helper the read and other served
         surfaces use, keeps design-time MSW and deployed API routing aligned. */
      let res: Record<string, unknown> = {};
      if (Object.keys(patch).length) {
        res = await updateBranding(patch as never, studioRequestOptions("settings.branding") as never) as unknown as Record<string, unknown>;
      }
      let nextAssets = assets;
      if (logoChanged) {
        if (pendingLogoFile) {
          const uploaded = await uploadBrandLogo(pendingLogoFile, studioRequestOptions("settings.branding") as never, pendingLogoFile.name);
          nextAssets = { ...assets, logo: uploaded.logoUrl };
        } else if (assets.logo === null) {
          await removeBrandLogo(studioRequestOptions("settings.branding") as never);
          nextAssets = { ...assets, logo: null };
        } else {
          throw new Error(t("settings.brandKit.logoFileRequired", "Choose a logo file to publish it."));
        }
      }
      logUpsert("branding", JSON.parse(_initial).b, brand as unknown as Record<string, unknown>, { id: "branding", name: t("settings.branding.branding", "Branding") });
      setAssets(nextAssets);
      setPendingLogoFile(null);
      setInitial(JSON.stringify({ b: brand, a: nextAssets }));
      invalidateTenantChrome();
      const warned = (res?.warnings as { field?: string }[] | undefined) ?? [];
      const title = t("settings.brandKit.brandKitPublished", "Brand kit published");
      const subtitle = warned.length
        ? `${t("settings.branding.savedExcept", "Not stored:")} ${warned.map((w) => w.field).filter(Boolean).join(", ")}`
        : t("settings.branding.eventsLandingPagesBookingFlowsEmailsAndKiosksNowInheritThese", "Events, landing pages, booking flows, emails and kiosks now inherit these.");
      toast({ tone: warned.length ? "warn" : "success", title, subtitle });
      announce(`${title} — ${subtitle}`);
    } catch (e) {
      console.warn("updateBranding failed:", e);
      const title = t("settings.branding.saveFailed", "Couldn't save the brand kit");
      const subtitle = e instanceof Error ? e.message : String(e);
      toast({ tone: "danger", title, subtitle });
      announce(`${title} — ${subtitle}`);
    } finally { setSaving(false); }
  };

  /** Throw the drafts away and go back to the last published kit. */
  const discard = () => {
    const before = JSON.parse(_initial) as { b: Brand; a: Record<string, string | null> };
    setBrand({ ...before.b });
    setAssets({ ...before.a });
    setPendingLogoFile(null);
    announce(t("settings.brandKit.draftsDiscarded", "Drafts discarded — back to the published kit."));
  };

  /** Reset canonical visual tokens immediately. Legal/sender/footer identity
   *  survives exactly as the backend contract specifies; logo and wallet
   *  availability are reset with the visual tokens. */
  const resetDefaults = async () => {
    if (saving || !window.confirm(t("settings.brandKit.confirmReset", "Reset colours, fonts, logo and wallet settings to platform defaults? Identity text will be preserved."))) return;
    setSaving(true);
    try {
      const response = await resetBranding(studioRequestOptions("settings.branding") as never) as unknown as Record<string, any>;
      const b = response.branding ?? response.data ?? response;
      const resetBrand: Brand = {
        ...brand,
        primary: b.brandColor ?? DEFAULT_BRAND.primary,
        accent: b.accentColor ?? DEFAULT_BRAND.accent,
        cover: b.coverColor ?? DEFAULT_BRAND.cover,
        headingFont: b.headingFont ?? DEFAULT_BRAND.headingFont,
        bodyFont: b.bodyFont ?? DEFAULT_BRAND.bodyFont,
        legalName: b.legalName ?? brand.legalName,
        senderName: b.emailSenderName ?? brand.senderName,
        footer: b.publicFooterText ?? brand.footer,
        appleWalletEnabled: b.appleWalletEnabled === true,
        googleWalletEnabled: b.googleWalletEnabled === true,
      };
      const resetAssets = { ...assets, logo: b.logoUrl || null, favicon: b.faviconUrl || null, ogImage: b.ogImage || null };
      setBrand(resetBrand);
      setAssets(resetAssets);
      setPendingLogoFile(null);
      setInitial(JSON.stringify({ b: resetBrand, a: resetAssets }));
      invalidateTenantChrome();
      const title = t("settings.branding.brandKitResetToDefaults", "Brand kit reset to defaults");
      toast({ tone: "success", title });
      announce(title);
    } catch (e) {
      const title = t("settings.brandKit.resetFailed", "Couldn't reset the Brand Kit");
      const subtitle = e instanceof Error ? e.message : String(e);
      toast({ tone: "danger", title, subtitle });
      announce(`${title} — ${subtitle}`);
    } finally { setSaving(false); }
  };

  return {
    brand, set, setBrand, assets, setAsset, setAssets, customFonts, legalNameSupported, brandKitDesigner,
    canWrite, dirty, draftCount, saving, save, discard, resetDefaults, readFailed,
    uploadImage, uploadSized, handleFontUpload,
    scanUrl, setScanUrl, scanning, scanError, scanResult, setScanResult, runScan, toggleScanItem, setAllScan, applyScan,
    announcement, announce,
  };
}
